import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { ArrowLeft, Play, HelpCircle, CheckCircle, X, AlertCircle, GraduationCap, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group';
import { Label } from '@/components/ui/label';
import { useTrainingQuizzes } from '../hooks/useTrainingQuizzes';
import { useQuizAttempts } from '../hooks/useQuizAttempts';
import { Confetti } from '@/components/ui/confetti';
import { supabase } from '@/integrations/supabase/client';
import type { ModuleWithProgress, TrainingAssignment } from '../types';

interface TrainingModulePlayerProps {
  module: ModuleWithProgress;
  assignment: TrainingAssignment;
  onBack: () => void;
}

export const TrainingModulePlayer: React.FC<TrainingModulePlayerProps> = ({
  module,
  assignment,
  onBack,
}) => {
  const { quizzes, isLoading: loadingQuizzes } = useTrainingQuizzes(module.id);
  const { submitQuiz } = useQuizAttempts();
  
  const [showQuiz, setShowQuiz] = useState(false);
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [answers, setAnswers] = useState<number[]>([]);
  const [result, setResult] = useState<{ passed: boolean; score: number; total: number; isLastModule?: boolean } | null>(null);
  const [showCelebration, setShowCelebration] = useState(false);

  const getVideoEmbedUrl = (url: string) => {
    const youtubeMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([^&\s]+)/);
    if (youtubeMatch) {
      return `https://www.youtube.com/embed/${youtubeMatch[1]}`;
    }
    const vimeoMatch = url.match(/vimeo\.com\/(\d+)/);
    if (vimeoMatch) {
      return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
    }
    return url;
  };

  const handleAnswerSelect = (questionIndex: number, answerIndex: number) => {
    const newAnswers = [...answers];
    newAnswers[questionIndex] = answerIndex;
    setAnswers(newAnswers);
  };

  const handleSubmitQuiz = async () => {
    const correctAnswers = quizzes.map(q => q.correct_answer_index);
    const response = await submitQuiz.mutateAsync({
      assignmentId: assignment.id,
      answers,
      correctAnswers,
    });
    
    // Check if this was the last module
    let isLastModule = false;
    if (response.passed) {
      const { data: pendingAssignments } = await supabase
        .from('training_assignments')
        .select('id')
        .eq('seller_id', assignment.seller_id)
        .neq('status', 'completed')
        .neq('id', assignment.id);
      
      isLastModule = !pendingAssignments || pendingAssignments.length === 0;
      
      if (isLastModule) {
        setShowCelebration(true);
      }
    }
    
    setResult({
      passed: response.passed,
      score: response.score,
      total: response.totalQuestions,
      isLastModule,
    });
  };

  const handleRetry = () => {
    setAnswers([]);
    setCurrentQuestionIndex(0);
    setResult(null);
    setShowQuiz(true);
  };

  const isCompleted = assignment.status === 'completed';

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="bg-gradient-to-br from-emerald-500/10 to-emerald-600/10 backdrop-blur-xl rounded-2xl border border-emerald-500/20 overflow-hidden"
    >
      {/* Header */}
      <div className="p-4 border-b border-white/10 flex items-center gap-3">
        <Button
          variant="ghost"
          size="sm"
          onClick={onBack}
          className="text-white/60 hover:text-white"
        >
          <ArrowLeft className="w-4 h-4 mr-1" />
          Volver
        </Button>
        <div className="flex-1">
          <h3 className="text-white font-semibold truncate">{module.title}</h3>
        </div>
        {isCompleted && (
          <span className="flex items-center gap-1 px-3 py-1 bg-emerald-500/20 text-emerald-400 rounded-full text-sm">
            <CheckCircle className="w-4 h-4" />
            Completado
          </span>
        )}
      </div>

      {/* Content */}
      {!showQuiz && !result ? (
        <div className="p-4 space-y-4">
          {/* Video Player */}
          <div className="aspect-video bg-black/50 rounded-xl overflow-hidden">
            <iframe
              src={getVideoEmbedUrl(module.video_url)}
              className="w-full h-full"
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
            />
          </div>

          {/* Description */}
          {module.description && (
            <p className="text-white/70">{module.description}</p>
          )}

          {/* Quiz Button */}
          {!isCompleted && quizzes.length > 0 && (
            <Button
              onClick={() => setShowQuiz(true)}
              className="w-full bg-emerald-500 hover:bg-emerald-600"
            >
              <HelpCircle className="w-4 h-4 mr-2" />
              Tomar Quiz ({quizzes.length} preguntas)
            </Button>
          )}

          {quizzes.length === 0 && !isCompleted && (
            <div className="text-center py-4 text-white/50">
              <AlertCircle className="w-8 h-8 mx-auto mb-2 opacity-50" />
              <p>Este módulo no tiene quiz configurado</p>
            </div>
          )}
        </div>
      ) : result ? (
        /* Quiz Results */
        <div className="p-6 text-center relative">
          <Confetti isActive={showCelebration} duration={4000} />
          
          {result.isLastModule && result.passed ? (
            /* Full Training Completion Celebration */
            <motion.div
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ type: 'spring', duration: 0.8 }}
            >
              <motion.div
                animate={{ 
                  rotate: [0, -10, 10, -10, 10, 0],
                  scale: [1, 1.1, 1]
                }}
                transition={{ duration: 1, repeat: 2 }}
                className="w-24 h-24 mx-auto mb-4 rounded-full bg-gradient-to-br from-yellow-400/30 to-amber-500/30 flex items-center justify-center border-2 border-yellow-400/50"
              >
                <GraduationCap className="w-12 h-12 text-yellow-400" />
              </motion.div>
              
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.3 }}
              >
                <h3 className="text-3xl font-bold text-yellow-400 mb-2">
                  🎉 ¡FELICITACIONES! 🎉
                </h3>
                <div className="flex items-center justify-center gap-2 mb-4">
                  <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                  <span className="text-xl text-yellow-400 font-semibold">Entrenamiento Completo</span>
                  <Star className="w-5 h-5 text-yellow-400 fill-yellow-400" />
                </div>
              </motion.div>
              
              <motion.p
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.5 }}
                className="text-white/80 text-lg mb-2"
              >
                Ahora eres un <span className="text-yellow-400 font-semibold">Experto VillaTech</span>
              </motion.p>
              
              <motion.p
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.7 }}
                className="text-white/60 text-sm mb-6"
              >
                Tu dedicación se refleja en tu ranking con +15 puntos bonus 🎓
              </motion.p>
              
              <motion.div
                initial={{ y: 20, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ delay: 0.9 }}
              >
                <Button onClick={onBack} className="bg-gradient-to-r from-yellow-500 to-amber-500 hover:from-yellow-600 hover:to-amber-600 text-black font-semibold px-8">
                  ¡Continuar! →
                </Button>
              </motion.div>
            </motion.div>
          ) : (
            /* Regular module completion or failure */
            <>
              <div className={`w-20 h-20 mx-auto mb-4 rounded-full flex items-center justify-center ${
                result.passed ? 'bg-emerald-500/20' : 'bg-red-500/20'
              }`}>
                {result.passed ? (
                  <CheckCircle className="w-10 h-10 text-emerald-400" />
                ) : (
                  <X className="w-10 h-10 text-red-400" />
                )}
              </div>
              
              <h3 className={`text-2xl font-bold mb-2 ${result.passed ? 'text-emerald-400' : 'text-red-400'}`}>
                {result.passed ? '¡Felicitaciones!' : 'Sigue intentando'}
              </h3>
              
              <p className="text-white/70 mb-4">
                Obtuviste {result.score} de {result.total} respuestas correctas
                ({((result.score / result.total) * 100).toFixed(0)}%)
              </p>

              {result.passed ? (
                <div className="space-y-3">
                  <p className="text-emerald-400">
                    Has completado este módulo exitosamente.
                  </p>
                  <Button onClick={onBack} className="bg-emerald-500 hover:bg-emerald-600">
                    Continuar
                  </Button>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-white/50 text-sm">
                    Necesitas 80% para aprobar. Tus respuestas han sido registradas para revisión.
                  </p>
                  <p className="text-white/40 text-xs">
                    Puedes ver el video nuevamente e intentar otra vez.
                  </p>
                  <div className="flex gap-2 justify-center">
                    <Button variant="outline" onClick={() => { setResult(null); setShowQuiz(false); }}>
                      Ver video
                    </Button>
                    <Button onClick={handleRetry} className="bg-blue-500 hover:bg-blue-600">
                      Reintentar Quiz
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      ) : (
        /* Quiz Questions */
        <div className="p-4 space-y-4">
          <div className="flex items-center justify-between mb-4">
            <span className="text-white/60 text-sm">
              Pregunta {currentQuestionIndex + 1} de {quizzes.length}
            </span>
            <Button
              variant="ghost"
              size="sm"
              onClick={() => { setShowQuiz(false); setAnswers([]); setCurrentQuestionIndex(0); }}
              className="text-white/40"
            >
              <X className="w-4 h-4" />
            </Button>
          </div>

          {/* Progress Dots */}
          <div className="flex gap-1 justify-center mb-4">
            {quizzes.map((_, index) => (
              <div
                key={index}
                className={`w-2 h-2 rounded-full transition-all ${
                  index === currentQuestionIndex
                    ? 'bg-emerald-400 w-6'
                    : answers[index] !== undefined
                    ? 'bg-emerald-400/50'
                    : 'bg-white/20'
                }`}
              />
            ))}
          </div>

          {/* Current Question */}
          <div className="bg-white/5 rounded-xl p-4">
            <h4 className="text-white font-medium mb-4">
              {quizzes[currentQuestionIndex]?.question}
            </h4>

            <RadioGroup
              value={answers[currentQuestionIndex]?.toString()}
              onValueChange={(value) => handleAnswerSelect(currentQuestionIndex, parseInt(value))}
            >
              {quizzes[currentQuestionIndex]?.options.map((option, optIndex) => (
                <div
                  key={optIndex}
                  className={`flex items-center gap-3 p-3 rounded-lg border transition-all cursor-pointer ${
                    answers[currentQuestionIndex] === optIndex
                      ? 'bg-emerald-500/20 border-emerald-500/50'
                      : 'bg-white/5 border-white/10 hover:bg-white/10'
                  }`}
                  onClick={() => handleAnswerSelect(currentQuestionIndex, optIndex)}
                >
                  <RadioGroupItem value={String(optIndex)} id={`q${currentQuestionIndex}-opt${optIndex}`} />
                  <Label
                    htmlFor={`q${currentQuestionIndex}-opt${optIndex}`}
                    className="flex-1 cursor-pointer text-white"
                  >
                    {option}
                  </Label>
                </div>
              ))}
            </RadioGroup>
          </div>

          {/* Navigation */}
          <div className="flex gap-2">
            {currentQuestionIndex > 0 && (
              <Button
                variant="outline"
                onClick={() => setCurrentQuestionIndex(i => i - 1)}
                className="flex-1"
              >
                Anterior
              </Button>
            )}
            
            {currentQuestionIndex < quizzes.length - 1 ? (
              <Button
                onClick={() => setCurrentQuestionIndex(i => i + 1)}
                disabled={answers[currentQuestionIndex] === undefined}
                className="flex-1 bg-emerald-500 hover:bg-emerald-600"
              >
                Siguiente
              </Button>
            ) : (
              <Button
                onClick={handleSubmitQuiz}
                disabled={answers.length !== quizzes.length || answers.some(a => a === undefined) || submitQuiz.isPending}
                className="flex-1 bg-emerald-500 hover:bg-emerald-600"
              >
                {submitQuiz.isPending ? 'Enviando...' : 'Enviar Respuestas'}
              </Button>
            )}
          </div>
        </div>
      )}
    </motion.div>
  );
};
